import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { toast } from 'sonner';

export interface WorkCost {
  id: string;
  shop_id: string;
  log_id: string;
  cost: number;
  created_at: string;
}

export interface WorkCostWithLog extends WorkCost {
  log?: {
    id: string;
    work_type: string;
    notes: string | null;
    performed_at: string;
  };
}

/**
 * Hook for work costs (maintenance/repair costs) - MANAGER ONLY
 * This is a dedicated hook separate from useAssetFinancials for cleaner separation
 */
export function useAssetWorkCosts(assetId: string | null) {
  const { activeShop } = useAuth();
  const { isManager } = usePermissions();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['asset-work-costs', assetId],
    queryFn: async () => {
      if (!assetId || !shopId || !isManager) return [];
      
      // Get log IDs for this asset first
      const { data: logs } = await supabase
        .from('shop_asset_maintenance_logs')
        .select('id, work_type, notes, performed_at')
        .eq('asset_id', assetId)
        .eq('shop_id', shopId);
      
      if (!logs || logs.length === 0) return [];
      
      const logIds = logs.map(l => l.id);
      const logMap = new Map(logs.map(l => [l.id, l]));
      
      const { data, error } = await supabase
        .from('shop_asset_work_costs')
        .select('*')
        .eq('shop_id', shopId)
        .in('log_id', logIds);
      
      if (error) throw error;
      
      // Join with log data
      return (data as WorkCost[]).map(cost => ({
        ...cost,
        log: logMap.get(cost.log_id),
      })) as WorkCostWithLog[];
    },
    enabled: !!assetId && !!shopId && isManager,
  });
}

/**
 * Get cost for a specific maintenance log
 */
export function useLogWorkCost(logId: string | null) {
  const { activeShop } = useAuth();
  const { isManager } = usePermissions();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['log-work-cost', logId],
    queryFn: async () => {
      if (!logId || !shopId || !isManager) return null;
      
      const { data, error } = await supabase
        .from('shop_asset_work_costs')
        .select('*')
        .eq('log_id', logId)
        .eq('shop_id', shopId)
        .maybeSingle();
      
      if (error) throw error;
      return data as WorkCost | null;
    },
    enabled: !!logId && !!shopId && isManager,
  });
}

export function useUpsertWorkCost() {
  const { activeShop } = useAuth();
  const { canEditAssets } = usePermissions();
  const queryClient = useQueryClient();
  const shopId = activeShop?.id;

  return useMutation({
    mutationFn: async (input: { log_id: string; cost: number; asset_id: string }) => {
      if (!shopId) throw new Error('No active shop');
      if (!canEditAssets) throw new Error('You do not have permission to edit asset work costs');

      // Upsert - check if exists first
      const { data: existing } = await supabase
        .from('shop_asset_work_costs')
        .select('id')
        .eq('log_id', input.log_id)
        .eq('shop_id', shopId)
        .maybeSingle();
      
      if (existing) {
        const { data, error } = await supabase
          .from('shop_asset_work_costs')
          .update({ cost: input.cost })
          .eq('id', existing.id)
          .select()
          .single();
        
        if (error) throw error;
        return { ...data, asset_id: input.asset_id };
      } else {
        const { data, error } = await supabase
          .from('shop_asset_work_costs')
          .insert({
            shop_id: shopId,
            log_id: input.log_id,
            cost: input.cost,
          })
          .select()
          .single();
        
        if (error) throw error;
        return { ...data, asset_id: input.asset_id };
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['asset-work-costs', data.asset_id] });
      queryClient.invalidateQueries({ queryKey: ['log-work-cost', data.log_id] });
      queryClient.invalidateQueries({ queryKey: ['work-costs', data.asset_id] });
      toast.success('Work cost saved');
    },
    onError: (error) => {
      toast.error('Failed to save work cost', { description: error.message });
    },
  });
}

/**
 * Calculate total work costs for an asset
 */
export function useTotalWorkCosts(assetId: string | null) {
  const { data: costs, isLoading } = useAssetWorkCosts(assetId);
  
  const total = costs?.reduce((sum, c) => sum + Number(c.cost), 0) ?? 0;
  
  return { total, isLoading };
}
